import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Award, Clock, Shield, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { useCMSContent, getSectionByType, getSection } from '../hooks/useCMSContent';
import { brandTitle } from '../seo/seoCore';

// Icon mapping
const iconMap = { Award, Clock, Shield, Users };

// Default values if CMS is empty
const defaultValues = [
  { icon: Award, title: 'Quality Craftsmanship', description: 'Every product is custom-made to your measurements and built to the highest standards.' },
  { icon: Clock, title: 'Fast Turnaround', description: 'Our Brooklyn-based team handles consultation, measuring and installation, keeping projects moving across New York City.' },
  { icon: Shield, title: 'Warranty Backed', description: 'Covered by the warranty provided with your order, with warranty service coordinated by our Brooklyn team.' },
  { icon: Users, title: 'Dedicated Support', description: 'Personal service from consultation through installation and beyond.' },
];

export const AboutPage = () => {
  const { sections, seo, loading } = useCMSContent('about');

  // Get CMS sections
  const heroSection = getSectionByType(sections, 'hero') || getSection(sections, 'about-hero');
  const storySection = getSection(sections, 'about-story') || sections?.find(s => s.title?.includes('Story'));
  const missionSection = getSection(sections, 'about-mission') || getSectionByType(sections, 'cta');
  const valuesSection = getSection(sections, 'about-values') || getSectionByType(sections, 'features');

  // Use CMS values or defaults
  const values = valuesSection?.items?.length > 0 
    ? valuesSection.items.map(item => ({
        ...item,
        icon: iconMap[item.icon] || Award
      }))
    : defaultValues;

  // Update document title from CMS SEO
  useEffect(() => {
    if (seo?.title) {
      document.title = brandTitle(seo.title);
    }
  }, [seo]);

  return (
    <Layout>
      {/* Hero - CMS Driven */}
      <section className="pt-32 pb-20 bg-neutral-50">
        <div className="container-xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="label-overline text-amber-700">{heroSection?.subtitle || 'About Us'}</span>
            <h1 className="mt-4 text-neutral-900">
              {heroSection?.title || "Brooklyn's Premier Custom Window Treatment Company"}
            </h1>
            <p className="mt-6 text-xl text-neutral-600 leading-relaxed">
              {heroSection?.content || "Apollo Shades is a Brooklyn-based company specializing in custom roller shades, motorized drapes, and Roman shades for residential and commercial projects, with consultation and installation throughout New York City."}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section - CMS Driven */}
      <section className="section-padding">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <img
                src={storySection?.image?.url || "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80"}
                alt={storySection?.image?.alt_text || "Apollo Shades showroom"}
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
            <div>
              <h2 className="text-neutral-900 mb-6 line-accent">{storySection?.title || 'Our Story'}</h2>
              <div className="space-y-4 text-neutral-600">
                {storySection?.content ? (
                  storySection.content.split('\n\n').map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))
                ) : (
                  <>
                    <p>
                      Founded with a vision to bring premium custom window treatments to the New York market, Apollo Shades has grown into a trusted partner for homeowners, designers, and contractors throughout the region.
                    </p>
                    <p>
                      Based in Brooklyn, we work closely with every client while offering the customization options they demand. Every product is custom-made to your measurements, ensuring a perfect fit for each unique space.
                    </p>
                    <p>
                      We combine traditional craftsmanship with modern automation technology, offering everything from classic cord-operated shades to fully motorized systems integrated with smart home platforms.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission - CMS Driven */}
      <section className="section-padding bg-neutral-900 text-white">
        <div className="container-lg text-center">
          <span className="label-overline text-amber-400">{missionSection?.title || 'Our Mission'}</span>
          <h2 className="mt-6 text-white max-w-4xl mx-auto">
            {missionSection?.content || "To deliver high-quality custom window treatments that combine design, function, and modern automation."}
          </h2>
        </div>
      </section>

      {/* Values - CMS Driven */}
      <section className="section-padding">
        <div className="container-xl">
          <div className="text-center mb-16">
            <span className="label-overline text-amber-700">{valuesSection?.subtitle || 'What Makes Us Different'}</span>
            <h2 className="mt-4 text-neutral-900">{valuesSection?.title || 'Our Commitment to Excellence'}</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center bg-neutral-100">
                    <IconComponent className="w-6 h-6 text-amber-700" strokeWidth={1.5} />
                  </div>
                  <h4 className="text-lg font-semibold text-neutral-900 mb-2">{value.title}</h4>
                  <p className="text-neutral-600 text-sm">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="section-padding bg-neutral-50">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="label-overline text-amber-700">Visit Our Showroom</span>
              <h2 className="mt-4 text-neutral-900 mb-6">Brooklyn Location</h2>
              <p className="text-neutral-600 mb-8">
                Visit our showroom to see our products in person, browse fabric samples, and speak with our design consultants about your project.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-amber-700 mt-1" />
                  <div>
                    <p className="font-medium text-neutral-900">Apollo Shades</p>
                    <p className="text-neutral-600">1 Brooklyn Navy Yard, Building 33, Suite 201B<br />Brooklyn, NY 11205</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Phone className="w-5 h-5 text-amber-700" />
                  <p className="text-neutral-600">(212) 209-0654</p>
                </div>
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-amber-700" />
                  <p className="text-neutral-600">info@apolloshades.com</p>
                </div>
              </div>
              <div className="mt-8 flex gap-4">
                <Link to="/contact">
                  <Button className="btn-primary">Schedule a Visit</Button>
                </Link>
                <Link to="/quote">
                  <Button className="btn-secondary">Request Quote</Button>
                </Link>
              </div>
            </div>
            <div className="aspect-square bg-neutral-200">
              <iframe
                src="https://www.google.com/maps?q=1+Brooklyn+Navy+Yard,+Building+33,+Brooklyn,+NY+11205&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Apollo Shades Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-neutral-900">
        <div className="container-xl text-center">
          <h2 className="text-white mb-6">Ready to Work Together?</h2>
          <p className="text-neutral-400 max-w-2xl mx-auto mb-10">
            Whether you're a homeowner looking for custom shades or a trade professional seeking a reliable partner, we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button className="btn-primary !bg-white !text-neutral-900 hover:!bg-neutral-100">
                Request a Quote
              </Button>
            </Link>
            <Link to="/trade">
              <Button className="btn-outline-light">
                Trade Program
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};
